import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Reusable empty-state card with icon, headline, hint bullets, primary + secondary CTA.
 * Designed to be high-converting without injecting any fake demo data.
 */
export default function EmptyState({
  icon: Icon,
  iconBg = "bg-blue-50 text-blue-600",
  title,
  description,
  bullets = [],
  primaryAction,
  secondaryAction,
  testId = "empty-state",
}) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 sm:p-12 text-center" data-testid={testId}>
      {Icon && (
        <div className={`mx-auto w-14 h-14 rounded-2xl ${iconBg} flex items-center justify-center mb-4`}>
          <Icon className="w-7 h-7" />
        </div>
      )}
      <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
      {description && <p className="text-sm text-slate-500 max-w-md mx-auto">{description}</p>}

      {bullets.length > 0 && (
        <ul className="mt-5 mx-auto inline-flex flex-col gap-2 text-left text-sm text-slate-600 max-w-md" data-testid={`${testId}-bullets`}>
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-6">
        {primaryAction && (
          primaryAction.to ? (
            <Link to={primaryAction.to}>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`${testId}-primary`}>
                {primaryAction.icon && <primaryAction.icon className="w-4 h-4 mr-1" />}
                {primaryAction.label}
              </Button>
            </Link>
          ) : (
            <Button onClick={primaryAction.onClick} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`${testId}-primary`}>
              {primaryAction.icon && <primaryAction.icon className="w-4 h-4 mr-1" />}
              {primaryAction.label}
            </Button>
          )
        )}
        {secondaryAction && (
          secondaryAction.to ? (
            <Link to={secondaryAction.to} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid={`${testId}-secondary`}>
              {secondaryAction.label} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          ) : (
            <button onClick={secondaryAction.onClick} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid={`${testId}-secondary`}>
              {secondaryAction.label} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )
        )}
      </div>
    </div>
  );
}
